import { mdiAccountPlusOutline, mdiClose, mdiMagnify } from '@mdi/js';
import Icon from '@mdi/react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useUserContext } from 'src/contexts/User.context';

import { FiltroUsuario, StatusUsuario } from '../../models/FiltroUsuario.model';
import { Funcionario } from '../../models/Funcionario.model';
import FuncionarioService from '../../services/Funcionario.service';
import { Colors } from '../../shared/colors';
import useInfiniteScroll from '../../shared/hooks/infiniteScroll';
import CardAssociado from '../components/CardAssociado';
import DrawerAssociarUsuario from '../features/associar-usuario/DrawerAssociarUsuario';
import DrawerTransferencias from '../features/realizar-transferencias/DrawerTranferencia';

const actionButtonSx = {
  color: Colors.secondary,
  fontSize: 15,
  fontWeight: 500,
  height: 36,
  minWidth: 0,
  px: 1.25,
  textTransform: 'none',
  whiteSpace: 'nowrap'
};

const ListaUsuarios = () => {
  const [openDrawerAssociacao, setOpenDrawerAssociacao] = useState(false);
  const [openDrawerTransferencia, setOpenDrawerTransferencia] = useState(false);
  const [busca, setBusca] = useState('');
  const [buscaAplicada, setBuscaAplicada] = useState('');
  const [somenteAtivos, setSomenteAtivos] = useState(true);
  const [usuariosSelecionados, setUsuariosSelecionados] = useState<Funcionario[]>([]);
  const { user } = useUserContext();

  const usuarioBloqueado = useMemo(() => {
    if (!user) return true;
    if ('statusBloqueio' in user) return user.statusBloqueio;
    return true;
  }, [user]);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      const termo = busca.trim();
      if (termo !== buscaAplicada) {
        setBuscaAplicada(termo);
        setUsuariosSelecionados([]);
      }
    }, 350);
    return () => window.clearTimeout(timeout);
  }, [busca, buscaAplicada]);

  const filtroAplicado = useMemo<FiltroUsuario>(() => {
    return {
      busca: buscaAplicada,
      status: somenteAtivos ? StatusUsuario.ATIVO : StatusUsuario.AMBOS
    };
  }, [buscaAplicada, somenteAtivos]);

  const {
    refresh,
    error,
    hasNext,
    loading,
    lastElementRef,
    items: contasAssociadas
  } = useInfiniteScroll<Funcionario, FiltroUsuario>({
    load: FuncionarioService.listar,
    search: filtroAplicado
  });

  const aplicarBusca = () => {
    const termo = busca.trim();
    if (termo === buscaAplicada) return;
    setBuscaAplicada(termo);
    setUsuariosSelecionados([]);
  };

  const limparBusca = () => {
    setBusca('');
    setBuscaAplicada('');
    setUsuariosSelecionados([]);
  };

  const fecharDrawerAssociacao = () => {
    refresh();
    setOpenDrawerAssociacao(false);
  };

  const fecharDrawerTransferencia = () => {
    refresh();
    setOpenDrawerTransferencia(false);
  };

  const selecionarUsuarios = useCallback((checked: boolean, funcionario: Funcionario) => {
    setUsuariosSelecionados((atuais) => {
      const jaSelecionado = atuais.some((item) => item.id === funcionario.id);
      if (checked) return jaSelecionado ? atuais : [...atuais, funcionario];
      return jaSelecionado ? atuais.filter((item) => item.id !== funcionario.id) : atuais;
    });
  }, []);

  const transferenciaDesabilitada =
    usuariosSelecionados.length < 1 || usuarioBloqueado || busca.trim() !== buscaAplicada;

  const alternarSomenteAtivos = () => {
    setSomenteAtivos((atual) => !atual);
    setUsuariosSelecionados([]);
  };

  return (
    <Grid container spacing={2} paddingY={2}>
      <Grid item xs={12}>
        <Box component="header" pb={1.75} borderBottom={`1px solid ${Colors.gray2}`}>
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <Typography variant="h5" color={Colors.secondary} fontWeight={700} whiteSpace="nowrap">
              Usuários associados
            </Typography>
            <TextField
              type="text"
              size="small"
              value={busca}
              onChange={(event) => setBusca(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') aplicarBusca();
              }}
              placeholder="Buscar nome, CPF ou cartão"
              aria-label="Buscar por nome, CPF ou número do cartão"
              sx={{
                flex: '1 1 300px',
                maxWidth: 380,
                minWidth: 0,
                ml: 'auto',
                '& .MuiOutlinedInput-root': {
                  bgcolor: Colors.white,
                  borderRadius: 1.5,
                  boxShadow: '0 4px 14px rgba(16, 42, 86, 0.11)',
                  overflow: 'hidden',
                  '& fieldset': { borderColor: Colors.border.divider },
                  '&:hover fieldset': { borderColor: Colors.border.divider },
                  '&.Mui-focused': { bgcolor: Colors.white },
                  '&.Mui-focused fieldset': { borderColor: Colors.border.divider, borderWidth: 1 }
                },
                '& input::placeholder': { color: Colors.gray1, fontSize: 13, opacity: 1 }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <IconButton
                      aria-label="Buscar usuários"
                      onClick={aplicarBusca}
                      edge="start"
                      disableRipple
                      sx={{
                        color: Colors.gray1,
                        '&:hover': { bgcolor: 'transparent' },
                        '&.Mui-focusVisible': { outline: `2px solid ${Colors.border.input}` }
                      }}
                    >
                      <Icon path={mdiMagnify} size={0.85} />
                    </IconButton>
                  </InputAdornment>
                ),
                endAdornment: busca ? (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="Limpar busca"
                      onClick={limparBusca}
                      edge="end"
                      disableRipple
                      sx={{
                        color: Colors.secondary,
                        '&:hover': { bgcolor: 'transparent' },
                        '&.Mui-focusVisible': { outline: `2px solid ${Colors.border.input}` }
                      }}
                    >
                      <Icon path={mdiClose} size={0.8} />
                    </IconButton>
                  </InputAdornment>
                ) : undefined
              }}
            />
            <FormControlLabel
              label="Ativos"
              control={
                <Checkbox
                  checked={somenteAtivos}
                  onChange={alternarSomenteAtivos}
                  size="small"
                  sx={{ color: Colors.secondary, '&.Mui-checked': { color: Colors.secondary } }}
                />
              }
              sx={{
                m: 0,
                '& .MuiFormControlLabel-label': { color: Colors.secondary, fontSize: 14 }
              }}
            />
          </Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" gap={1} mt={2}>
            <Button
              data-tour-pj="associate-user"
              startIcon={<Icon path={mdiAccountPlusOutline} size={0.75} />}
              onClick={() => setOpenDrawerAssociacao(true)}
              sx={{ ...actionButtonSx, fontSize: { xs: 13, sm: 15 }, px: { xs: 0.5, sm: 1.25 } }}
            >
              Associar usuário
            </Button>
            <Button
              data-tour-pj="transfer-balance"
              disabled={transferenciaDesabilitada}
              startIcon={
                <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M21,9L17,5V8H10V10H17V13Z" fill={Colors.moneyGreen} />
                  <path d="M7,11L3,15L7,19V16H14V14H7V11Z" fill={Colors.error.dark} />
                </svg>
              }
              onClick={() => setOpenDrawerTransferencia(true)}
              sx={{ ...actionButtonSx, fontSize: { xs: 13, sm: 15 }, px: { xs: 0.5, sm: 1.25 } }}
            >
              {usuariosSelecionados.length > 0
                ? `Transferir saldo (${usuariosSelecionados.length})`
                : 'Transferir saldo'}
            </Button>
          </Box>
        </Box>
      </Grid>

      <Grid item xs={12}>
        {contasAssociadas.map((contaFuncionario, index, arr) => {
          const lastElement = arr.length === index + 1;
          const ref = lastElement && hasNext ? lastElementRef : null;
          return (
            <div
              ref={ref}
              key={contaFuncionario.id}
              data-tour-pj={index === 0 ? 'associated-users' : undefined}
            >
              <CardAssociado
                onUpdate={refresh}
                onChecked={selecionarUsuarios}
                contaFuncionario={contaFuncionario}
              />
            </div>
          );
        })}
      </Grid>

      <Grid item xs={12}>
        {loading && (
          <Box
            role="status"
            display="flex"
            justifyContent="center"
            alignItems="center"
            gap={1.5}
            minHeight={contasAssociadas.length === 0 ? 160 : 64}
          >
            <CircularProgress size={24} sx={{ color: Colors.primary.main }} />
            <Typography color={Colors.gray1}>
              {contasAssociadas.length === 0
                ? 'Buscando usuários...'
                : 'Carregando mais usuários...'}
            </Typography>
          </Box>
        )}
        {!loading && error && (
          <Box
            data-tour-pj="associated-users"
            display="flex"
            justifyContent="center"
            alignItems="center"
            gap={2}
            flexWrap="wrap"
          >
            <Typography color={Colors.gray1}>Não foi possível carregar os usuários.</Typography>
            <Button onClick={refresh} sx={actionButtonSx}>
              Tentar novamente
            </Button>
          </Box>
        )}
        {!loading && !error && contasAssociadas.length === 0 && (
          <Typography
            data-tour-pj="associated-users"
            variant="body1"
            fontWeight={500}
            color={Colors.gray1}
            textAlign="center"
          >
            {buscaAplicada
              ? 'Nenhum usuário encontrado para esta busca'
              : somenteAtivos
              ? 'Nenhum usuário ativo encontrado'
              : 'Nenhum usuário associado'}
          </Typography>
        )}
      </Grid>

      <DrawerAssociarUsuario open={openDrawerAssociacao} onClose={fecharDrawerAssociacao} />
      <DrawerTransferencias
        usuarios={usuariosSelecionados}
        open={openDrawerTransferencia}
        onClose={fecharDrawerTransferencia}
      />
    </Grid>
  );
};

export default ListaUsuarios;
